import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { SearchPanelProps } from "../../types/searchPanelTypes";
import { FL_RATING_OPTIONS } from "../../constants/flRatingOptions";
import MultipleSliders from "../Slider/MultipleSliders";
import { sliderConfigs } from "../../constants/sliderConfigs";
import { PunishmentFilters } from "../../types/punishmentFilterTypes";
import { draftSliderConfigs } from "../../constants/draftSliderConfigs";

export default function SearchPanel({
  embarrassmentLevel,
  setEmbarrassmentLevel,
  riskLevel,
  setRiskLevel,
  longTermEffects,
  setLongTermEffects,
  setIsFilterApplied,
  selectedFLRating,
  setSelectedFLRating,
  contentType,
}: SearchPanelProps) {
  const [isSearchActive, setIsSearchActive] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // We still use tempFilters for instant UI feedback within the panel
  const [tempFilters, setTempFilters] = useState<PunishmentFilters>({
    shame: embarrassmentLevel,
    risk: riskLevel,
    effects: longTermEffects,
    rating: selectedFLRating,
  });

  // ✨ KEY CHANGE: Debounce filter application
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      // Apply the temporary filters to the parent component's state
      setEmbarrassmentLevel(tempFilters.shame);
      setRiskLevel(tempFilters.risk);
      setLongTermEffects(tempFilters.effects);
      setSelectedFLRating(tempFilters.rating);

      // Determine if any filter is active and update the parent
      const isAnyFilterActive =
        tempFilters.shame > 0 ||
        tempFilters.risk > 0 ||
        tempFilters.effects > 0 ||
        tempFilters.rating !== "";
      setIsFilterApplied(isAnyFilterActive);
    }, 500); // Wait for 500ms of inactivity before applying filters

    // Cleanup: Clear the timeout if the user makes another change
    return () => {
      clearTimeout(debounceTimer);
    };
  }, [
    tempFilters, // This effect runs whenever tempFilters changes
    setEmbarrassmentLevel,
    setRiskLevel,
    setLongTermEffects,
    setSelectedFLRating,
    setIsFilterApplied,
  ]);

  const handleToggleSearch = useCallback(() => {
    setIsSearchActive((prev) => !prev);
  }, []);

  const handleRatingSelect = useCallback((rating: string) => {
    setTempFilters((prev) => ({
      ...prev,
      rating: prev.rating === rating ? "" : rating,
    }));
  }, []);

  // handleApply is no longer needed

  const handleReset = useCallback(() => {
    const initialFilters = { shame: 0, risk: 0, effects: 0, rating: "" };
    // Reset local and parent state immediately
    setTempFilters(initialFilters);
    setEmbarrassmentLevel(0);
    setRiskLevel(0);
    setLongTermEffects(0);
    setSelectedFLRating("");
    setIsFilterApplied(false);
  }, [
    setEmbarrassmentLevel,
    setRiskLevel,
    setLongTermEffects,
    setSelectedFLRating,
    setIsFilterApplied,
  ]);

  // This effect for closing the panel on outside click remains unchanged
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node)
      ) {
        setIsSearchActive(false);
      }
    };

    if (isSearchActive) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSearchActive]);

  const ratingOptions = useMemo(
    () =>
      FL_RATING_OPTIONS.map(({ value, label, level }) => (
        <li
          key={value}
          className={tempFilters.rating === value ? "active" : ""}
          onClick={() => handleRatingSelect(value)}
        >
          <em className={`levelBlk badge-${level}`}>{`L${level}`}</em>
          <span>{label}</span>
        </li>
      )),
    [tempFilters.rating, handleRatingSelect]
  );

  return (
    <div ref={panelRef}>
      <button
        className={`searchControl ${isSearchActive ? "active" : ""}`}
        onClick={handleToggleSearch}
      >
        SearchControl
      </button>

      {isSearchActive && (
        <div className="searchItems">
          <div className="itemHeading">Severity Ratings:</div>
          <ul className="severityList">{ratingOptions}</ul>
          <hr />
          <div className="itemHeading">Severity Level:</div>
          <div className="levelSlideWrap">
            <MultipleSliders<PunishmentFilters, "shame" | "risk" | "effects">
              tempFilters={tempFilters}
              setTempFilters={(partialUpdate) =>
                setTempFilters((prev) => ({ ...prev, ...partialUpdate }))
              }
              sliderConfigs={
                contentType === "draftIdeas"
                  ? draftSliderConfigs
                  : sliderConfigs
              }
            />
          </div>
          <div className="btnWrap">
            <button className="txtBtn" onClick={handleReset}>
              Reset
            </button>
            {/* The "Apply" button is now removed */}
          </div>
        </div>
      )}
    </div>
  );
}